import posthog from 'posthog-js';
import React, { useMemo, useState } from 'react';
import { useInterval } from 'react-use';

import { Kbd } from '~/modules/elements/akui/kbd';
import { Menu } from '~/modules/elements/akui/menu';
import { NavRemoteControl } from '~/modules/elements/nav-controls';
import GameState from '~/modules/game-engine/game-state/game-state';
import beatToMs from '~/modules/game-engine/game-state/helpers/beat-to-ms';
import useDebounce from '~/modules/hooks/use-debounce';
import useKeyboardNav, { KeyboardNavContext } from '~/modules/hooks/use-keyboard-nav';
import { getLastNoteEnd } from '~/modules/songs/utils/notes-selectors';
import { MobilePhoneModeSetting, useSettingValue } from '~/routes/settings/settings-state';

interface Props {
  onSongEnd?: () => void;
  isEnabled: boolean;
  /** Opens the pause menu — surfaced as the mirrored keyboard's Back control (the TV still uses Backspace). */
  onOpenPauseMenu?: () => void;
}

const SHOW_OUTRO_THRESHOLD_MS = 15_000;

function SkipOutro({ onSongEnd, isEnabled, onOpenPauseMenu }: Props) {
  const [mobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
  const duration = GameState.getDuration();

  const singingEndBeat = useMemo(
    () => Math.max(...GameState.getPlayers().map((player) => getLastNoteEnd(player.getLastNotesSection()))),
    [],
  );

  const canSkip = useMemo(() => {
    const singingEndTime = beatToMs(singingEndBeat, GameState.getSong()!) + (GameState.getSong()?.gap ?? 0);

    return isEnabled && duration * 1000 > singingEndTime + SHOW_OUTRO_THRESHOLD_MS;
  }, [duration, singingEndBeat, isEnabled]);

  const [currentBeat, setCurrentBeat] = useState(0);
  const [skipping, setSkipping] = useState(false);
  const [videoLeftMs, setVideoLeftMs] = useState(0);

  useInterval(() => {
    setCurrentBeat(GameState.getCurrentBeat());
    setVideoLeftMs(Math.max(0, duration * 1000 - GameState.getCurrentTime(false)));
  }, 1_000);

  const shouldBeVisible = useDebounce(canSkip && currentBeat > singingEndBeat, 5_000) && !skipping;

  const skipOutro = () => {
    setSkipping(true);
    if (onSongEnd) {
      setTimeout(onSongEnd, 700);
    }

    const { artist, title } = GameState.getSong()!;
    posthog.capture('outroSkipped', { name: `${artist} - ${title}`, artist, title });
  };

  // A one-action mirror: the prompt element carries the skip action (so physical Enter still skips),
  // plus a remote-only Back that opens the pause menu — the phone otherwise has no Back in mirror mode.
  const { register } = useKeyboardNav({
    enabled: shouldBeVisible,
    title: 'Skip outro',
    primary: 'skip-outro',
    titleIcon: 'play',
  });
  // `focused` is stripped: the prompt is a plain HelpText, not a menu item, so it would leak onto the DOM.
  const { focused: _focused, ...skipNav } = register('skip-outro', skipOutro, 'Skip outro', true, {
    control: { type: 'button', label: 'Skip outro', icon: 'fastForward' },
  });

  return !mobilePhoneMode && canSkip ? (
    <KeyboardNavContext value={register}>
      <div
        className={`pointer-events-none fixed inset-0 z-[4] flex flex-col items-center justify-center gap-7 transition-all duration-500 ${shouldBeVisible ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}>
        <Menu.HelpText
          {...skipNav}
          className="w-full p-2 text-center text-[1.875rem] [text-shadow:0_0_2rem_black]"
          style={{ textShadow: '0 0 2rem black' }}
          data-test="skip-outro-info"
          data-visible={shouldBeVisible}>
          Press <Kbd>Enter</Kbd> to skip the outro
        </Menu.HelpText>
        {/* Because in a room someone always asks what happens if nobody presses anything. */}
        <span className="stroke-text text-[1.25rem] font-bold text-white/50" data-test="skip-outro-otherwise">
          or let it play &mdash; results in {Math.ceil(videoLeftMs / 1000)} s
        </span>
      </div>
      {onOpenPauseMenu && (
        <NavRemoteControl
          name="skip-outro-pause"
          control={{ type: 'button', label: 'Pause game', icon: 'pause' }}
          onClick={onOpenPauseMenu}
        />
      )}
    </KeyboardNavContext>
  ) : null;
}

export default React.memo(SkipOutro);
